import React from 'react';
import { Link } from 'react-router-dom';
import './PostBodyRenderer.scss';

interface PostBodyRendererProps {
  body: string;
  className?: string;
}

// Matches #hashtag and @mention tokens in-line with surrounding text
const TOKEN_RE = /(#[a-zA-Z][a-zA-Z0-9_]{0,49}|@[a-zA-Z0-9_]{1,50})/g;

/**
 * Renders post/comment body text with clickable #hashtags and @mentions.
 * Preserves newlines. Plain text parts are never wrapped in extra elements.
 */
const PostBodyRenderer: React.FC<PostBodyRendererProps> = ({ body, className }) => {
  if (!body) return <p className={`post-body-text${className ? ` ${className}` : ''}`} />;

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;

  while ((m = TOKEN_RE.exec(body)) !== null) {
    const token = m[0];
    const start = m.index;

    // Emit plain text before this token (with newline support)
    if (start > last) {
      nodes.push(...splitNewlines(body.slice(last, start), nodes.length));
    }

    if (token.startsWith('#')) {
      const tag = token.slice(1).toLowerCase();
      nodes.push(
        <Link key={start} to={`/feed/hashtag/${tag}`} className="post-hashtag">
          {token}
        </Link>,
      );
    } else {
      const username = token.slice(1);
      nodes.push(
        <Link key={start} to={`/profile/@${username}`} className="post-mention">
          {token}
        </Link>,
      );
    }

    last = start + token.length;
  }

  // Remaining plain text
  if (last < body.length) {
    nodes.push(...splitNewlines(body.slice(last), nodes.length));
  }

  return <p className={`post-body-text${className ? ` ${className}` : ''}`}>{nodes}</p>;
};

/** Convert \n characters into <br /> elements. */
function splitNewlines(text: string, keyOffset: number): React.ReactNode[] {
  return text.split('\n').flatMap((line, i) =>
    i === 0 ? [line] : [<br key={`br-${keyOffset}-${i}`} />, line],
  );
}

export default PostBodyRenderer;
